import React, {useEffect, useMemo, useState} from 'react';
import {
    Dialog, DialogContent, DialogActions, TextField, Button, Box, Typography,
    Grid, Paper, Chip, IconButton, Avatar, LinearProgress, Tooltip, useMediaQuery
} from '@mui/material';
import {alpha, styled} from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {DateRange} from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import {addDemande, getDisabledDates} from '../../../../services/DemandeService';
import {notifyError, notifySuccess} from '../../../../components/CustomToast';

dayjs.locale('fr');

const GlassCard = styled(Paper)(({theme}) => ({
    borderRadius: 16,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(
        theme.palette.background.paper, 0.75
    )} 100%)`,
}));

const Header = ({onClose}) => (
    <Box sx={(t) => ({
        px: 3, pt: 3, pb: 2, color: '#fff', position: 'relative',
        background: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
    })}>
        <Box sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background:
                'radial-gradient(900px 240px at 10% -20%, rgba(255,255,255,.22), transparent),' +
                'radial-gradient(520px 180px at 110% 0%, rgba(255,255,255,.15), transparent)',
        }}/>
        <Box display="flex" alignItems="center" justifyContent="space-between" position="relative">
            <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar variant="rounded" sx={{width: 46, height: 46, bgcolor: alpha('#fff', .22), color: '#fff'}}>
                    <CalendarMonthIcon/>
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={800}>Demande de congé</Typography>
                    <Typography variant="caption" sx={{opacity: .9}}>Sélectionnez vos dates et ajoutez une note</Typography>
                </Box>
            </Box>
            <IconButton onClick={onClose} sx={{color: '#fff'}} aria-label="Fermer">
                <CloseRoundedIcon/>
            </IconButton>
        </Box>
    </Box>
);

const AccentBar = () => (
    <Box sx={(t) => ({
        height: 4,
        background: `linear-gradient(90deg, ${t.palette.primary.light}, ${t.palette.secondary.main})`
    })}/>
);

const startOfTodayLocal = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const quickRange = (days = 1) => {
    const start = startOfTodayLocal();
    const end = new Date(start);
    end.setDate(start.getDate() + (days - 1));
    return {startDate: start, endDate: end, key: 'selection'};
};

const VacationRequestModal = ({open, onClose, onSubmit, collaborateurId}) => {
    const isSmall = useMediaQuery((t) => t.breakpoints.down('md'));
    const monthsToShow = isSmall ? 1 : 2;

    const [range, setRange] = useState([{startDate: null, endDate: null, key: 'selection'}]);
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const [disabledDates, setDisabledDates] = useState([]);

    useEffect(() => {
        let active = true;
        const load = async () => {
            if (!open || !collaborateurId) { setDisabledDates([]); return; }
            try {
                const { data } = await getDisabledDates(collaborateurId);
                const list = data?.disabledDates || [];
                if (!active) return;
                const arr = list.map((s) => {
                    const [y, m, d] = s.split('-').map(Number);
                    return new Date(y, (m || 1) - 1, d || 1);
                });
                setDisabledDates(arr);
            } catch (e) {
            }
        };
        load();
        return () => { active = false; };
    }, [open, collaborateurId]);

    const start = range[0].startDate;
    const end = range[0].endDate;
    const hasBoth = Boolean(start && end);
    const invalidRange = hasBoth ? end < start : false;

    const durationDays = useMemo(() => {
        if (!hasBoth || invalidRange) return null;
        const d1 = dayjs(start), d2 = dayjs(end);
        return Math.max(0, d2.diff(d1, 'day') + 1);
    }, [start, end, hasBoth, invalidRange]);

    const hasClash = useMemo(() => {
        if (!hasBoth || invalidRange) return false;
        const set = new Set(disabledDates.map(d => dayjs(d).format('YYYY-MM-DD')));
        let cur = dayjs(start).startOf('day');
        const last = dayjs(end).startOf('day');
        while (cur.isBefore(last) || cur.isSame(last)) {
            if (set.has(cur.format('YYYY-MM-DD'))) return true;
            cur = cur.add(1, 'day');
        }
        return false;
    }, [hasBoth, invalidRange, start, end, disabledDates]);

    const maxChars = 400;
    const charPct = Math.min(100, Math.round((description.length / maxChars) * 100));
    const canSubmit = hasBoth && !invalidRange && !loading && !hasClash;

    const applyPreset = (days) => setRange([quickRange(days)]);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        if (hasClash) {
            notifyError("Les dates choisies chevauchent une demande existante.");
            return;
        }
        const payload = {
            type: 'demande de conge',
            sousType: null,
            collaborateur: [collaborateurId],
            dateDebut: start,
            dateFin: end,
            description: description?.trim() || null,
        };
        try {
            setLoading(true);
            await addDemande(payload);
            notifySuccess('Demande envoyée avec succès !');
            onSubmit?.();
            onClose();
        } catch (err) {
            const apiMsg =
                   err?.response?.data?.error?.detail ??
                     err?.response?.data?.detail ??
                    err?.response?.data?.message ??
                   (err?.response?.status === 409 ? "La période sélectionnée est déjà couverte par une autre demande." : null);
              notifyError(apiMsg || "Erreur lors de l'envoi de la demande");
        } finally {
            setLoading(false);
            setRange([{startDate: null, endDate: null, key: 'selection'}]);
            setDescription('');
        }
    };

    const today = useMemo(() => startOfTodayLocal(), []);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            slotProps={{ paper: {
                sx: (t) => ({
                    borderRadius: 4, overflow: 'hidden', boxShadow: '0 26px 70px rgba(0,0,0,0.28)',
                    background: `linear-gradient(180deg, ${alpha(t.palette.background.paper, .9)} 0%, ${t.palette.background.paper} 100%)`,
                })
            } }}
        >
            <Header onClose={onClose}/>
            <AccentBar/>

            <DialogContent sx={{p: 3, maxHeight: '75vh', overflow: 'auto'}}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={7}>
                        <GlassCard sx={{p: 2, maxHeight: 520, overflow: 'auto'}}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                <TodayRoundedIcon color="primary"/>
                                <Typography fontWeight={700}>Période</Typography>
                            </Box>

                            <Box
                                sx={{
                                    '& .rdrCalendarWrapper': {width: '100%', background: 'transparent'},
                                    '& .rdrMonths': {display: 'flex', flexWrap: 'wrap', gap: 2},
                                    '& .rdrMonth': {flex: 1, minWidth: 260},
                                }}
                            >
                                <DateRange
                                    ranges={range}
                                    onChange={(r) => setRange([r.selection])}
                                    moveRangeOnFirstSelection={false}
                                    showPreview
                                    months={monthsToShow}
                                    direction="horizontal"
                                    weekdayDisplayFormat="EEEEE"
                                    rangeColors={['#1976d2']}
                                    minDate={today}
                                    disabledDates={disabledDates}
                                />
                            </Box>

                            <Box mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Typography variant="caption" color="text.secondary">Raccourcis :</Typography>
                                <Chip size="small" label="Aujourd’hui" onClick={() => applyPreset(1)}/>
                                <Chip size="small" label="2 jours" onClick={() => applyPreset(2)}/>
                                <Chip size="small" label="1 semaine" onClick={() => applyPreset(7)}/>
                            </Box>
                        </GlassCard>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <GlassCard sx={{p: 2, height: '100%'}}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                <EventAvailableRoundedIcon color="primary"/>
                                <Typography fontWeight={700}>Aperçu</Typography>
                            </Box>

                            <Typography variant="body2" color="text.secondary">
                                {start ? dayjs(start).format('DD/MM/YYYY') : '—'} → {end ? dayjs(end).format('DD/MM/YYYY') : '—'}
                            </Typography>

                            <Box mt={1.5}>
                                <Chip
                                    size="small"
                                    color={durationDays ? 'primary' : 'default'}
                                    label={durationDays ? `${durationDays} jour(s)` : 'Durée —'}
                                />
                            </Box>

                            <Box
                                mt={2}
                                p={2}
                                borderRadius={2}
                                sx={(t) => ({
                                    border: `1px dashed ${alpha(t.palette.primary.main, .35)}`,
                                    bgcolor: alpha(t.palette.primary.main, .04)
                                })}
                            >
                                <Box display="flex" alignItems="center" gap={1}>
                                    <TipsAndUpdatesRoundedIcon color="primary" fontSize="small"/>
                                    <Typography variant="caption" color="text.secondary">
                                        Conseil : ajoutez une note utile pour la validation
                                    </Typography>
                                </Box>

                                <TextField
                                    sx={{mt: 1.5}}
                                    label="Description (facultatif)"
                                    placeholder="Ex. : Déplacement familial, RDV administratif…"
                                    multiline
                                    rows={4}
                                    fullWidth
                                    value={description}
                                    onChange={(e) => e.target.value.length <= maxChars && setDescription(e.target.value)}
                                    helperText={`${description.length}/${maxChars} caractères`} slotProps={{ formHelperText: {sx: {textAlign: 'right'}} }}
                                />

                                <LinearProgress
                                    variant="determinate"
                                    value={charPct}
                                    sx={(t) => ({
                                        mt: 1, height: 6, borderRadius: 2, bgcolor: alpha(t.palette.primary.main, .08),
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 2,
                                            background: `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                                        },
                                    })}
                                />
                            </Box>
                        </GlassCard>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{px: 3, pb: 2, pt: 2}}>
                <Button
                    onClick={onClose}
                    sx={{borderRadius: 3, textTransform: 'none', fontWeight: 500, color: 'text.secondary'}}
                    disabled={loading}
                >
                    Annuler
                </Button>

                <Tooltip
                    title={
                        !hasBoth
                            ? 'Sélectionnez une plage de dates'
                            : invalidRange
                                ? 'La fin doit être ≥ au début'
                                : hasClash
                                    ? 'Les dates choisies chevauchent une demande existante'
                                    : ''
                    }
                    placement="top"
                    arrow
                    disableHoverListener={hasBoth && !invalidRange && !hasClash}
                >
          <span>
            <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit}
                startIcon={<RocketLaunchRoundedIcon/>}
                sx={(t) => ({
                    px: 3, borderRadius: 3, textTransform: 'none', fontWeight: 'bold', minWidth: 170,
                    background: `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                    boxShadow: '0px 8px 22px rgba(0,0,0,0.18)',
                    '&:hover': {background: `linear-gradient(90deg, ${t.palette.secondary.main}, ${t.palette.primary.main})`},
                })}
            >
              {loading ? 'Envoi…' : 'Envoyer la demande'}
            </Button>
          </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

export default VacationRequestModal;
